import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getUpcoming } from "./PlannerService";
import { updateItemStatus } from "./PlannerService";
import "./UpcomingDates.css";

const TODAY = new Date().toISOString().slice(0, 10);

const TYPE_META = {
  PAYMENT:        { icon: "💳", label: "Payment",       color: "#16a34a" },
  IMPORTANT_DATE: { icon: "📅", label: "Important Date", color: "#0891b2" },
  REMINDER:       { icon: "🔔", label: "Reminder",       color: "#6d5ef9" },
  TODO:           { icon: "✅", label: "Task",           color: "#2563eb" },
};

const PAYMENT_TYPE_ICONS = {
  RENT: "🏠", EMI: "🏦", SIP: "📈",
  SUBSCRIPTION: "📱", BILL: "📄", INSURANCE: "🛡️",
};

function getDate(item) {
  return item.dueDate || item.eventDate || item.paymentDate || "";
}

function daysFrom(iso) {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const d = new Date(iso + "T00:00:00");
  return Math.round((d - today) / 86400000);
}

function relLabel(n, iso) {
  if (n === 0)  return { text: "Today",       cls: "urgent" };
  if (n === 1)  return { text: "Tomorrow",    cls: "soon"   };
  if (n <= 7)   return { text: `In ${n}d`,    cls: "week"   };
  if (n <= 30)  return { text: `In ${n}d`,    cls: "month"  };
  return             { text: iso.slice(5).replace("-", "/"), cls: "later" };
}

function sectionKey(n) {
  if (n === 0)  return "today";
  if (n === 1)  return "tomorrow";
  if (n <= 7)   return "week";
  if (n <= 14)  return "next_week";
  if (n <= 30)  return "month";
  return "later";
}

const SECTION_ORDER  = ["today", "tomorrow", "week", "next_week", "month", "later"];
const SECTION_LABELS = {
  today: "Today", tomorrow: "Tomorrow", week: "This week",
  next_week: "Next week", month: "This month", later: "Later",
};

export default function UpcomingDates() {
  const navigate = useNavigate();
  const [items, setItems]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays]       = useState(60);
  const [error, setError]     = useState(null);

  const load = async (d = days) => {
    setLoading(true); setError(null);
    try { setItems(await getUpcoming(d)); }
    catch { setError("Could not load upcoming items."); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(days); }, [days]);

  const markDone = async (item) => {
    setItems(prev => prev.filter(i => i.id !== item.id));
    try { await updateItemStatus(item.id, "DONE"); }
    catch { load(days); }
  };

  // Group
  const grouped = {};
  for (const item of items) {
    const d = getDate(item);
    if (!d) continue;
    const k = sectionKey(daysFrom(d));
    if (!grouped[k]) grouped[k] = [];
    grouped[k].push(item);
  }
  const sections = SECTION_ORDER.filter(k => grouped[k]?.length);

  // Summary
  const todayCount   = (grouped.today   ?? []).length;
  const paymentTotal = items
    .filter(i => i.type === "PAYMENT" && i.amount > 0)
    .reduce((s, i) => s + Number(i.amount), 0);

  return (
    <div className="ud-root">
      <div className="ud-header">
        <div>
          <h2 className="ud-title">Upcoming</h2>
          <p className="ud-sub">{items.length} items in the next {days} days</p>
        </div>
        <div className="ud-actions">
          <div className="ud-range-tabs">
            {[30, 60, 90].map(d => (
              <button key={d} className={`ud-range-tab ${days === d ? "active" : ""}`} onClick={() => setDays(d)}>
                {d}d
              </button>
            ))}
          </div>
          <button className="ud-add-btn" onClick={() => navigate("/planner")}>+ Add</button>
        </div>
      </div>

      {/* Summary bar */}
      {items.length > 0 && (
        <div className="ud-summary">
          {todayCount > 0 && (
            <span className="ud-chip urgent">⚡ {todayCount} today</span>
          )}
          {paymentTotal > 0 && (
            <span className="ud-chip payment">
              💳 ₹{paymentTotal >= 1000 ? (paymentTotal / 1000).toFixed(1) + "k" : paymentTotal.toLocaleString("en-IN")} due
            </span>
          )}
          {items.filter(i => i.type === "IMPORTANT_DATE").length > 0 && (
            <span className="ud-chip dates">
              📅 {items.filter(i => i.type === "IMPORTANT_DATE").length} dates
            </span>
          )}
        </div>
      )}

      {loading && <div className="ud-loading">Loading…</div>}
      {error   && <div className="ud-error">{error} <button onClick={() => load()}>Retry</button></div>}

      {!loading && sections.length === 0 && (
        <div className="ud-empty">
          <span className="ud-empty-icon">🗓️</span>
          <p className="ud-empty-title">Nothing upcoming</p>
          <p className="ud-empty-sub">Add payments, important dates and reminders to track them here</p>
          <button className="ud-empty-btn" onClick={() => navigate("/planner")}>Add first item</button>
        </div>
      )}

      {!loading && sections.map(key => (
        <div key={key} className="ud-section">
          <div className="ud-section-hdr">
            <span className="ud-section-label">{SECTION_LABELS[key]}</span>
            <div className="ud-section-line" />
          </div>

          {grouped[key].map(item => {
            const d   = getDate(item);
            const n   = daysFrom(d);
            const rl  = relLabel(n, d);
            const tm  = TYPE_META[item.type] ?? TYPE_META.TODO;
            const pti = item.paymentType ? PAYMENT_TYPE_ICONS[item.paymentType] : null;
            const icon = pti ?? tm.icon;

            return (
              <div key={item.id} className={`ud-row ${n <= 2 ? "urgent-row" : ""}`}>
                <div className="ud-row-stripe" style={{ background: tm.color }} />
                <div className="ud-row-icon" style={{ background: tm.color + "18" }}>
                  <span>{icon}</span>
                </div>
                <div className="ud-row-body">
                  <div className="ud-row-title">{item.title}</div>
                  <div className="ud-row-meta">
                    <span className="ud-type-pill" style={{ color: tm.color, background: tm.color + "14" }}>
                      {item.paymentType ?? tm.label}
                    </span>
                    {item.projectName && <span className="ud-project">{item.projectName}</span>}
                    {item.recurrence && item.recurrence !== "NONE" && (
                      <span className="ud-recurrence">↩ {item.recurrence.toLowerCase()}</span>
                    )}
                  </div>
                </div>
                <div className="ud-row-right">
                  <span className={`ud-countdown ${rl.cls}`}>{rl.text}</span>
                  {item.amount > 0 && (
                    <span className="ud-amount">₹{Number(item.amount).toLocaleString("en-IN")}</span>
                  )}
                </div>
                <div className="ud-row-actions">
                  <button
                    className="ud-done-btn"
                    title="Mark done"
                    onClick={() => markDone(item)}
                  >✓</button>
                  <button
                    className="ud-edit-btn"
                    title="Open in planner"
                    onClick={() => navigate("/planner")}
                  >›</button>
                </div>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}
